import { NavLink, Outlet, Link } from "react-router-dom";
import { Sun, Moon, Monitor, LogIn, LogOut, ExternalLink, Sparkles } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useAuth, shortAddr } from "@/lib/auth";
import { useI18n, SUPPORTED_LOCALES, type Locale } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GlobalSearch } from "@/components/GlobalSearch";
import { cn } from "@/lib/cn";

export function Layout() {
  return (
    <div className="min-h-screen bg-bg text-fg">
      <TopNav />
      <main className="container py-8">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function TopNav() {
  const { t } = useI18n();
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur-md">
      <div className="container flex h-14 items-center gap-6">
        <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
          <div className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-fg">
            <Sparkles className="h-4 w-4" />
          </div>
          <span>TCGPricer</span>
        </Link>
        <nav className="hidden items-center gap-1 text-sm lg:flex">
          <NavItem to="/">{t("nav.home") || "Home"}</NavItem>
          <NavItem to="/cards">{t("nav.catalog") || "Catalog"}</NavItem>
          <NavItem to="/marketplace">{t("nav.marketplace") || "Marketplace"}</NavItem>
          <NavItem to="/sell">{t("nav.sell_card") || "Sell"}</NavItem>
          <NavItem to="/top-movers">{t("nav.top_movers") || "Top movers"}</NavItem>
          <NavItem to="/manual-entries?kind=sealed">{t("nav.sealed") || "Scellé"}</NavItem>
          <NavItem to="/desirability">{t("nav.desirability") || "Desirability"}</NavItem>
          <NavItem to="/stats">{t("nav.stats") || "Stats"}</NavItem>
          <NavItem to="/me">{t("nav.my_profile") || "Profile"}</NavItem>
        </nav>
        <div className="ml-auto flex flex-1 items-center justify-end gap-2 sm:gap-3">
          {/* Recherche globale — présente sur toutes les pages. */}
          <div className="hidden w-full max-w-xs sm:block">
            <GlobalSearch variant="nav" />
          </div>
          <LocaleSelect />
          <ThemeToggle />
          <AuthBadge />
        </div>
      </div>
    </header>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        cn(
          "rounded-md px-3 py-1.5 transition-colors",
          isActive ? "bg-surface-2 text-fg" : "text-muted-fg hover:bg-surface-2 hover:text-fg",
        )
      }
    >
      {children}
    </NavLink>
  );
}

function ThemeToggle() {
  const { pref, setPref } = useTheme();
  const next = pref === "light" ? "dark" : pref === "dark" ? "system" : "light";
  const Icon = pref === "light" ? Sun : pref === "dark" ? Moon : Monitor;
  return (
    <Button
      variant="ghost"
      size="icon"
      title={`Theme: ${pref} → ${next}`}
      onClick={() => setPref(next)}
      aria-label="Toggle theme"
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

function LocaleSelect() {
  const { locale, setLocale } = useI18n();
  return (
    <div className="hidden items-center sm:flex">
      <div className="inline-flex h-8 rounded-md border border-border bg-surface p-0.5 text-[11px]">
        {SUPPORTED_LOCALES.map((l) => (
          <button
            key={l}
            onClick={() => setLocale(l as Locale)}
            className={cn(
              "rounded-sm px-2 font-medium uppercase transition-colors",
              locale === l ? "bg-surface-2 text-fg" : "text-muted-fg hover:text-fg",
            )}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function AuthBadge() {
  const { wallet, walletChecksum, loading, connect, logout } = useAuth();
  if (loading) {
    return <div className="h-8 w-24 animate-pulse rounded-md bg-surface-2" />;
  }
  if (!wallet) {
    return (
      <Button size="sm" variant="primary" onClick={() => connect().catch((e) => alert(e.message))}>
        <LogIn className="h-3.5 w-3.5" />
        Connect
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <Link
        to="/me"
        title={walletChecksum ?? wallet}
        className="rounded-md border border-border bg-surface px-2 py-1 font-mono text-xs hover:bg-surface-2"
      >
        {shortAddr(walletChecksum ?? wallet)}
      </Link>
      <Button size="icon" variant="ghost" onClick={() => logout()} title="Sign out" aria-label="Sign out">
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border py-6">
      <div className="container flex flex-wrap items-center justify-between gap-3 text-xs text-muted-fg">
        <div className="flex items-center gap-2">
          <Badge tone="info" variant="soft">V1 · PR-219</Badge>
          <span>TCGPricer</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/install" className="inline-flex items-center gap-1 hover:text-fg">
            Chrome extension <ExternalLink className="h-3 w-3" />
          </Link>
          <a href="/admin" className="inline-flex items-center gap-1 hover:text-fg">
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
}
